import { getDictationSoundEnabled } from '@/hooks/query/useQueryUser';
import {
  getDictationBridgePort,
  type DictationStore,
  type SoundsSlice,
} from '@heidi/dictate';
import { type StateCreator } from 'zustand';
import { dictationLogger } from './dictationLogger';

/** Sound playback: native Rust audio (desktop+FF) or HTMLAudioElement fallback. */

interface AudioCache {
  element: HTMLAudioElement | null;
  ready: boolean;
}

const audioCache: Record<string, AudioCache> = {};

const DICTATION_SOUND_VOLUME = 0.6;

const preloadAudioWeb = (src: string): void => {
  if (typeof window === 'undefined') return;
  if (audioCache[src]) return;

  const audio = new Audio(src);
  audio.preload = 'auto';

  const cacheEntry: AudioCache = { element: audio, ready: false };
  audioCache[src] = cacheEntry;

  audio.addEventListener('canplaythrough', () => {
    cacheEntry.ready = true;
  });

  audio.addEventListener('error', (e) => {
    dictationLogger.error({
      event_type: 'DICTATION_SOUND_PRELOAD_FAILED',
      details: { src, error: e },
    });
  });

  try {
    audio.load();
  } catch (error) {
    dictationLogger.error({
      event_type: 'DICTATION_SOUND_LOAD_FAILED',
      details: { src, error },
    });
  }
};

const playAudioWeb = (src: string, eventType: string): void => {
  let cache = audioCache[src];

  if (!cache?.element) {
    preloadAudioWeb(src);
    cache = audioCache[src];
    if (!cache?.element) return;

    if (!cache.ready) {
      const element = cache.element;
      element.addEventListener(
        'canplaythrough',
        () => {
          playWhenReady(element, eventType);
        },
        { once: true }
      );
      return;
    }
  }

  if (cache.element) {
    playWhenReady(cache.element, eventType);
  }
};

const playWhenReady = (audio: HTMLAudioElement, eventType: string): void => {
  audio.pause();
  audio.currentTime = 0;
  audio.volume = DICTATION_SOUND_VOLUME;
  void audio.play().catch((err: unknown) => {
    dictationLogger.error({
      event_type: eventType,
      details: { error: err },
    });
  });
};

const SOUND_PATHS = {
  start: '/assets/mp3/web-dictation-sound/dictate_start.mp3',
  stop: '/assets/mp3/web-dictation-sound/dictate_stop.mp3',
  error: '/assets/mp3/web-dictation-sound/dictate_error.mp3',
} as const;

// Preload web audio (FF check happens at runtime)
if (typeof window !== 'undefined') {
  Object.values(SOUND_PATHS).forEach(preloadAudioWeb);
}

export const createSoundsSlice: StateCreator<
  DictationStore,
  [],
  [],
  SoundsSlice
> = () => {
  return {
    startSound: () => {
      if (!getDictationSoundEnabled()) return;
      if (getDictationBridgePort().shouldUseNativeAudio()) {
        void getDictationBridgePort()
          .playDictationSound('start')
          .catch((err: unknown) => {
            dictationLogger.error({
              event_type: 'DICTATION_START_SOUND_FAILED',
              details: { error: err },
            });
          });
      } else {
        playAudioWeb(SOUND_PATHS.start, 'DICTATION_START_SOUND_FAILED');
      }
    },

    stopSound: () => {
      if (!getDictationSoundEnabled()) return;
      if (getDictationBridgePort().shouldUseNativeAudio()) {
        void getDictationBridgePort()
          .playDictationSound('stop')
          .catch((err: unknown) => {
            dictationLogger.error({
              event_type: 'DICTATION_STOP_SOUND_FAILED',
              details: { error: err },
            });
          });
      } else {
        playAudioWeb(SOUND_PATHS.stop, 'DICTATION_STOP_SOUND_FAILED');
      }
    },

    errorSound: () => {
      if (!getDictationSoundEnabled()) return;
      if (getDictationBridgePort().shouldUseNativeAudio()) {
        void getDictationBridgePort()
          .playDictationSound('error')
          .catch((err: unknown) => {
            dictationLogger.error({
              event_type: 'DICTATION_ERROR_SOUND_FAILED',
              details: { error: err },
            });
          });
      } else {
        playAudioWeb(SOUND_PATHS.error, 'DICTATION_ERROR_SOUND_FAILED');
      }
    },
  };
};
